import { getErrorMessage } from '@features/shared/api/client'
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@features/shared/components/Toast/ToastContext'
import type {
  SidebarFavoritesResponse,
  SidebarHiddenPage,
  SidebarHiddenResponse,
} from '../api/contracts'
import { getHiddenPages, hidePage, unhidePage } from '../api/hidden.api'
import { sidebarQueryKeys } from '../api/queryKeys'
import { normalizeFavoritePath } from '../logic/favorites'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'
import { useMenuFiltersStore } from '../stores/useMenuFiltersStore'
import { inTurn, useFavoriteChanges, useFavoritesQuery } from './useFavorites'

const HIDDEN_STALE_MS = 60_000
/** How long an undo toast (a hidden page, a deleted folder) offers Undo. */
export const UNDO_TOAST_MS = 8000
const NO_PATHS: ReadonlySet<string> = new Set()

/** The pages the user keeps out of the sidebar's menus. */
export function useHiddenPagesQuery() {
  return useQuery({
    queryKey: sidebarQueryKeys.hidden,
    queryFn: getHiddenPages,
    staleTime: HIDDEN_STALE_MS,
  })
}

/** The addresses of the user's hidden pages. */
export function useHiddenPaths(): ReadonlySet<string> {
  const { data } = useHiddenPagesQuery()
  return useMemo(() => (data ? new Set(data.hidden.map((page) => page.path)) : NO_PATHS), [data])
}

/** The addresses of the user's favorites. */
export function useFavoritePaths(): ReadonlySet<string> {
  const { data } = useFavoritesQuery()
  return useMemo(
    () => (data ? new Set(data.favorites.map((favorite) => favorite.path)) : NO_PATHS),
    [data]
  )
}

/** The page at `path` is hidden from the sidebar's menus. */
export function useIsHidden(path: string | null): boolean {
  const hidden = useHiddenPaths()
  return path !== null && hidden.has(normalizeFavoritePath(path))
}

type HiddenChange = { kind: 'hide' | 'unhide'; path: string }

let latestChangeId = 0

function applyChange(pages: readonly SidebarHiddenPage[], change: HiddenChange) {
  if (change.kind === 'unhide') return pages.filter((page) => page.path !== change.path)
  if (pages.some((page) => page.path === change.path)) return [...pages]
  return [...pages, { path: change.path, hidden_at: new Date().toISOString() }]
}

/**
 * Hides pages from the sidebar's menus and shows them again. Each change
 * shows at once and goes to the server in turn with favorite changes; one
 * that fails puts the lists back and says so. Hiding a favorite takes its
 * heart away (never both). A row hidden while its menu is in use stays,
 * dimmed, until the pointer leaves, and a toast offers to undo it.
 */
export function useHiddenChanges() {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const { add: addFavorite } = useFavoriteChanges()
  const mutation = useMutation<
    SidebarHiddenResponse,
    Error,
    HiddenChange,
    {
      previous: SidebarHiddenResponse | undefined
      previousFavorites: SidebarFavoritesResponse | undefined
      changeId: number
      /** The page was a favorite: hiding it took the heart away. */
      unfavorited: boolean
    }
  >({
    mutationFn: (change) =>
      inTurn(() => (change.kind === 'hide' ? hidePage(change.path) : unhidePage(change.path))),
    onMutate: async (change) => {
      const changeId = ++latestChangeId
      await queryClient.cancelQueries({ queryKey: sidebarQueryKeys.hidden })
      const previous = queryClient.getQueryData<SidebarHiddenResponse>(sidebarQueryKeys.hidden)
      queryClient.setQueryData<SidebarHiddenResponse>(sidebarQueryKeys.hidden, {
        hidden: applyChange(previous?.hidden ?? [], change),
        updated_at: previous?.updated_at ?? null,
      })
      const previousFavorites = queryClient.getQueryData<SidebarFavoritesResponse>(
        sidebarQueryKeys.favorites
      )
      const unfavorited =
        change.kind === 'hide' &&
        !!previousFavorites?.favorites.some((favorite) => favorite.path === change.path)
      if (unfavorited && previousFavorites) {
        await queryClient.cancelQueries({ queryKey: sidebarQueryKeys.favorites })
        queryClient.setQueryData<SidebarFavoritesResponse>(sidebarQueryKeys.favorites, {
          ...previousFavorites,
          favorites: previousFavorites.favorites.filter(
            (favorite) => favorite.path !== change.path
          ),
        })
      }
      return { previous, previousFavorites, changeId, unfavorited }
    },
    onError: (error, _change, context) => {
      if (context?.changeId === latestChangeId && context.previous !== undefined) {
        queryClient.setQueryData(sidebarQueryKeys.hidden, context.previous)
      } else {
        void queryClient.invalidateQueries({ queryKey: sidebarQueryKeys.hidden })
      }
      addToast({
        type: 'error',
        title: "Couldn't update hidden pages",
        message: getErrorMessage(error),
      })
    },
    onSuccess: (response, _change, context) => {
      // A later change is already showing; its own answer brings the list.
      if (context?.changeId === latestChangeId) {
        queryClient.setQueryData(sidebarQueryKeys.hidden, response)
      }
    },
    onSettled: (_response, _error, _change, context) => {
      if (context?.unfavorited) {
        void queryClient.invalidateQueries({ queryKey: sidebarQueryKeys.favorites })
      }
    },
  })

  const { mutate } = mutation
  return useMemo(() => {
    const unhide = (path: string) => mutate({ kind: 'unhide', path: normalizeFavoritePath(path) })
    /** Hides a page; `name` is what the undo toast calls it. */
    const hide = (rawPath: string, name: string) => {
      const path = normalizeFavoritePath(rawPath)
      const wasFavorite = !!queryClient
        .getQueryData<SidebarFavoritesResponse>(sidebarQueryKeys.favorites)
        ?.favorites.some((favorite) => favorite.path === path)
      if (useGlobalSidebarStore.getState().drawerInUse) {
        useMenuFiltersStore.getState().hold(path)
      }
      mutate({ kind: 'hide', path })
      addToast({
        type: 'info',
        title: `${name} is hidden`,
        message: wasFavorite
          ? 'It no longer shows in the sidebar, and it left your favorites.'
          : 'It no longer shows in the sidebar.',
        duration: UNDO_TOAST_MS,
        actions: [
          {
            label: 'Undo',
            // Hearting brings it back as a favorite and shows it (never both).
            onClick: () => (wasFavorite ? addFavorite(path) : unhide(path)),
          },
        ],
      })
    }
    return { hide, unhide }
  }, [addFavorite, addToast, mutate, queryClient])
}
